import { RolesEnum } from '../types/RolesEnum';
import { getRoleDisplayLabel } from './specialistRoleModuleHelper';
import { buildModuleFieldLabel, isFlowRecordPublished } from './flowEditPageHelper';

const isFieldEditReason = (reasonDetail = '') => {
  const text = String(reasonDetail || '');
  return text.startsWith('FLOW-EDIT:')
    || text.startsWith('FLOW-UPDATE-EXCEL:')
    || text.startsWith('FLOW-REASSIGN:');
};

export const isModificationEvent = (event = {}) => {
  if (event.ruleflow_id) {
    return false;
  }
  if (isFieldEditReason(event.reason_detail)) {
    return true;
  }
  const diff = event.diff_snapshot;
  if (!diff || typeof diff !== 'object') {
    return false;
  }
  const keys = Object.keys(diff);
  return keys.length > 0 && !keys.every((key) => key === 'state_catalog_id');
};

export const isTransitionEvent = (event = {}) => Boolean(event.ruleflow_id);

export const splitHistoryEvents = (events = []) => {
  const transitions = [];
  const modifications = [];

  (events || []).forEach((event) => {
    if (isModificationEvent(event)) {
      modifications.push(event);
    } else {
      transitions.push(event);
    }
  });

  return { transitions, modifications };
};

export const resolveCatalogName = (catalogId, catalogs = []) => {
  if (catalogId === null || catalogId === undefined || catalogId === '') {
    return '—';
  }
  const match = (catalogs || []).find((item) => Number(item.id) === Number(catalogId));
  return match?.name || match?.description || match?.label || String(catalogId);
};

export const resolveRoleLabel = (catalogroleId, roles = [], t) => {
  const match = (roles || []).find((item) => Number(item.id) === Number(catalogroleId));
  if (!match?.name) {
    return catalogroleId ? `#${catalogroleId}` : '—';
  }
  return getRoleDisplayLabel(match.name, t);
};

export const resolveUserLabel = (event = {}) => {
  const user = event.user;
  if (!user) {
    return event.user_id ? `#${event.user_id}` : '—';
  }
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName || user.user_name || user.email || `#${user.id}`;
};

const formatUserReference = (userId, usersById = {}, roleLabel = '') => {
  if (userId === '—' || userId === null || userId === undefined || userId === '') {
    return '—';
  }

  const numericId = Number(userId);
  const user = usersById[userId] || usersById[numericId];
  const name = user ? resolveUserLabel({ user }) : `#${userId}`;

  if (roleLabel && roleLabel !== '—') {
    return `${name} (${roleLabel})`;
  }

  return user ? `${name} (#${numericId})` : `#${userId}`;
};

const normalize = (value = '') => String(value).trim().toLowerCase();

export const resolveStateName = (stateCatalogId, flowEngine = {}) => {
  return (flowEngine.states || []).find((item) => Number(item.id) === Number(stateCatalogId))?.name || '';
};

export const resolveRecordMotivo = (record, flowEngine = {}, t) => {
  const safeRecord = record || {};

  if (String(safeRecord.reason_detail || '').trim()) {
    return safeRecord.reason_detail;
  }

  if (safeRecord.reason_detail_catalog_id) {
    const catalogs = [
      ...(flowEngine.pendingReasons || []),
      ...(flowEngine.cancelReasons || []),
      ...(flowEngine.normalReasons || [])
    ];
    const match = catalogs.find((item) => Number(item.id) === Number(safeRecord.reason_detail_catalog_id));
    if (match?.name || match?.description) {
      return match.name || match.description;
    }
  }

  return t('flow.motivo.not-available', 'Sin motivo registrado');
};

export const hasRecordMotivo = (record, flowEngine = {}) => {
  const safeRecord = record || {};
  if (String(safeRecord.reason_detail || '').trim()) {
    return true;
  }
  if (!safeRecord.reason_detail_catalog_id) {
    return false;
  }
  const catalogs = [
    ...(flowEngine.pendingReasons || []),
    ...(flowEngine.cancelReasons || []),
    ...(flowEngine.normalReasons || [])
  ];
  const match = catalogs.find((item) => Number(item.id) === Number(safeRecord.reason_detail_catalog_id));
  return Boolean(match?.name || match?.description);
};

export const resolveStateReason = (record, flowEngine = {}, t) => {
  const safeRecord = record || {};
  const state = resolveCatalogName(safeRecord.state_catalog_id, flowEngine.states);
  const pending = resolveCatalogName(safeRecord.pending_reason_detail_catalog_id, flowEngine.pendingReasons);
  const cancel = resolveCatalogName(safeRecord.cancel_reason_detail_catalog_id, flowEngine.cancelReasons);

  const stateName = (flowEngine.states || []).find((item) => Number(item.id) === Number(safeRecord.state_catalog_id))?.name || '';

  if (normalize(stateName) === 'pendiente') {
    return {
      state,
      reasonLabel: t('general.flow-engine.history.reason-pending', 'Motivo de pendiente'),
      reasonValue: safeRecord.pending_reason_detail || pending,
      detail: safeRecord.pending_reason_detail || ''
    };
  }

  if (normalize(stateName) === 'rechazado') {
    return {
      state,
      reasonLabel: t('general.flow-engine.history.reason-rejected', 'Motivo de rechazo'),
      reasonValue: safeRecord.cancel_reason_detail || cancel,
      detail: safeRecord.cancel_reason_detail || ''
    };
  }

  return {
    state,
    reasonLabel: t('flow.motivo.label', 'Motivo'),
    reasonValue: resolveRecordMotivo(safeRecord, flowEngine, t),
    detail: ''
  };
};

export const resolveBandejaLabel = (record, roles = [], t, selectedRole = null, flowEngine = {}) => {
  const safeRecord = record || {};
  if (isFlowRecordPublished(safeRecord, flowEngine)) {
    return t('flow.cmi.out-of-tray', 'Fuera de bandeja');
  }
  const roleId = safeRecord.current_role_catalog_id;
  if (!roleId) {
    return t('flow.cmi.out-of-tray', 'Fuera de bandeja');
  }

  let match = (roles || []).find((item) => Number(item.id) === Number(roleId));
  if (!match?.name && selectedRole && Number(selectedRole.id) === Number(roleId)) {
    match = selectedRole;
  }
  if (match?.name) {
    return getRoleDisplayLabel(match.name, t);
  }
  return `#${roleId}`;
};

export const resolveBandejaTitle = (record, roles = [], t, selectedRole = null, flowEngine = {}) => {
  if (isFlowRecordPublished(record, flowEngine) || !record?.current_role_catalog_id) {
    return t('flow.cmi.out-of-tray', 'Fuera de bandeja');
  }
  const roleLabel = resolveBandejaLabel(record, roles, t, selectedRole, flowEngine);
  return t('flow.bandeja.title', 'Bandeja del {{role}}', { role: roleLabel });
};

const formatYearValue = (value) => {
  if (value === null || value === undefined || value === '' || value === '—') {
    return '—';
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return String(value.getFullYear());
  }
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-\d{2}-\d{2}/);
  if (iso) {
    return iso[1];
  }
  if (/^\d{4}$/.test(text)) {
    return text;
  }
  return text;
};

export const formatDateTime = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

export const resolveEventTimestamp = (event = {}) =>
  event?.created_at ?? event?.createdAt ?? event?.updated_at ?? event?.updatedAt ?? null;

const FLOW_DIFF_FIELD_LABELS = {
  state_catalog_id: 'flow.diff.state',
  current_user_id: 'flow.diff.assigned-user',
  current_role_catalog_id: 'flow.diff.last-role',
  current_roleflow_id: 'flow.diff.last-roleflow',
  last_transition_at: 'flow.diff.last-transition',
  pending_reason_detail_catalog_id: 'flow.diff.reason-catalog',
  cancel_reason_detail_catalog_id: 'flow.diff.reason-catalog',
  reason_detail_catalog_id: 'flow.diff.reason-catalog',
  reason_detail: 'flow.diff.reason-detail',
  origin_manager_user_id: 'flow.diff.origin-manager',
  published_at: 'flow.diff.published-at',
  is_visible: 'flow.diff.is-visible'
};

const FLOW_DIFF_FIELD_DEFAULTS = {
  state_catalog_id: 'Estado',
  current_user_id: 'Usuario asignado',
  current_role_catalog_id: 'Último rol',
  last_transition_at: 'Última transición',
  pending_reason_detail_catalog_id: 'Motivo (catálogo)',
  cancel_reason_detail_catalog_id: 'Motivo (catálogo)',
  reason_detail_catalog_id: 'Motivo (catálogo)',
  reason_detail: 'Motivo',
  origin_manager_user_id: 'Gestor de origen',
  published_at: 'Fecha de publicación',
  is_visible: 'Visible al usuario final'
};

const HIDDEN_DIFF_FIELDS = new Set([
  'current_flow_id',
  'current_ruleflow_id',
  'current_roleflow_id',
  'reassignment'
]);

export const formatDiffEntriesForDisplay = ({
  diffSnapshot = {},
  fieldLabelResolver,
  flowEngine = {},
  roles = [],
  t,
  recordStateCatalogId = null,
  usersById = {},
  catalogLookups = {}
}) => {
  if (!diffSnapshot || typeof diffSnapshot !== 'object') {
    return [];
  }

  const stateName = normalize(resolveStateName(recordStateCatalogId, flowEngine));

  return Object.entries(diffSnapshot)
    .filter(([field]) => {
      if (HIDDEN_DIFF_FIELDS.has(field)) {
        return false;
      }
      if (field === 'pending_reason_detail_catalog_id' || field === 'cancel_reason_detail_catalog_id' || field === 'reason_detail_catalog_id') {
        return stateName === 'pendiente' || stateName === 'rechazado';
      }
      return true;
    })
    .map(([field, change]) => {
      let previous = change?.previous ?? '—';
      let next = change?.next ?? '—';

      if (field === 'state_catalog_id') {
        previous = resolveCatalogName(previous, flowEngine.states);
        next = resolveCatalogName(next, flowEngine.states);
      } else if (field === 'current_role_catalog_id') {
        previous = resolveRoleLabel(previous, roles, t);
        next = resolveRoleLabel(next, roles, t);
      } else if (field === 'current_user_id') {
        const previousRole = resolveRoleLabel(diffSnapshot.current_role_catalog_id?.previous, roles, t);
        const nextRole = resolveRoleLabel(diffSnapshot.current_role_catalog_id?.next, roles, t);
        previous = formatUserReference(previous, usersById, previousRole);
        next = formatUserReference(next, usersById, nextRole);
      } else if (field === 'origin_manager_user_id') {
        const originRole = getRoleDisplayLabel(RolesEnum.SPECIALIST_MANAGER, t);
        previous = formatUserReference(previous, usersById, originRole);
        next = formatUserReference(next, usersById, originRole);
      } else if (catalogLookups[field]) {
        previous = resolveCatalogName(previous, catalogLookups[field]);
        next = resolveCatalogName(next, catalogLookups[field]);
      } else if (/_year$/.test(field)) {
        previous = formatYearValue(previous);
        next = formatYearValue(next);
      } else if (field === 'last_transition_at' || field === 'published_at') {
        previous = formatDateTime(previous);
        next = formatDateTime(next);
      } else if (field === 'pending_reason_detail_catalog_id' || field === 'cancel_reason_detail_catalog_id' || field === 'reason_detail_catalog_id') {
        const catalogs = [...(flowEngine.pendingReasons || []), ...(flowEngine.cancelReasons || [])];
        previous = resolveCatalogName(previous, catalogs);
        next = resolveCatalogName(next, catalogs);
      }

      const i18nKey = FLOW_DIFF_FIELD_LABELS[field];
      const label = i18nKey
        ? t(i18nKey, FLOW_DIFF_FIELD_DEFAULTS[field] || field)
        : (fieldLabelResolver ? fieldLabelResolver(field) : field);

      return { field, label, previous, next };
    });
};

export const formatDiffEntries = (diffSnapshot = {}, fieldLabelResolver = (key) => key) => {
  if (!diffSnapshot || typeof diffSnapshot !== 'object') {
    return [];
  }

  return Object.entries(diffSnapshot).map(([field, change]) => ({
    field,
    label: fieldLabelResolver(field),
    previous: change?.previous ?? '—',
    next: change?.next ?? '—'
  }));
};

export const findPreviousProfileRequest = (events = [], currentRoleId, roles = [], t) => {
  const { transitions } = splitHistoryEvents(events);
  const currentRole = Number(currentRoleId);

  const candidate = transitions.find((event) => {
    const eventRole = Number(event.catalogrole_id);
    const hasNote = Boolean(event.reason_detail);
    return hasNote && eventRole && eventRole !== currentRole;
  });

  if (!candidate) {
    return null;
  }

  return {
    roleLabel: resolveRoleLabel(candidate.catalogrole_id, roles, t),
    userLabel: resolveUserLabel(candidate),
    note: candidate.reason_detail || '',
    createdAt: candidate.created_at
  };
};

export const findLastModificationByRole = (events = [], roleId) => {
  const { modifications } = splitHistoryEvents(events);
  const targetRole = Number(roleId);

  return modifications.find((event) => Number(event.catalogrole_id) === targetRole) || modifications[0] || null;
};

export const mergeRoleCatalogs = (...sources) => {
  const map = new Map();

  sources.flat().forEach((role) => {
    if (role?.id) {
      map.set(Number(role.id), role);
    }
  });

  return Array.from(map.values());
};

export const sortHistoryEventsDesc = (events = []) =>
  [...(events || [])].sort((a, b) => {
    const dateA = new Date(a.created_at || 0).getTime();
    const dateB = new Date(b.created_at || 0).getTime();
    if (dateB !== dateA) {
      return dateB - dateA;
    }
    return Number(b.id || 0) - Number(a.id || 0);
  });

export const resolveHistoryEventTitle = (event = {}, t) => {
  if (event.ruleflow_id && event.rule) {
    return event.rule.description || event.rule.value || t('flow.history.transition', 'Transición de flujo');
  }

  if (String(event.reason_detail || '').startsWith('FLOW-UPDATE-EXCEL:')) {
    return t('flow.history.excel-edit', 'Actualización desde Excel');
  }

  if (String(event.reason_detail || '').startsWith('FLOW-REASSIGN:')) {
    return t('flow.history.reassign', 'Reasignación de cadena');
  }

  if (isModificationEvent(event)) {
    return t('flow.history.field-edit', 'Edición de campos');
  }

  if (event.reason_detail?.startsWith('FLOW-IMPORT:')) {
    return t('flow.history.import', 'Importación masiva');
  }

  return t('flow.history.event', 'Evento del motor de flujos');
};

export const resolveHistoryAssignmentLine = (event = {}, t, usersById = {}) => {
  const delivery = String(event.assignment_delivery || '').trim().toLowerCase();
  if (!delivery) {
    return '';
  }
  const ruleCode = event.winning_rule_code || '';
  const ruleLabel = ruleCode
    ? t(`flow.assignment.rule.${ruleCode}`, ruleCode)
    : '';
  if (delivery === 'pool') {
    return [
      t('flow.history.assignment.pool', 'Quedó en el mostrador (autoasignación).'),
      ruleLabel
    ].filter(Boolean).join(' · ');
  }
  const assigned = event.assignedUser
    || usersById[event.assigned_user_id]
    || usersById[Number(event.assigned_user_id)];
  const name = assigned
    ? resolveUserLabel({ user: assigned })
    : (event.assigned_user_id ? `#${event.assigned_user_id}` : '');
  const personLine = name
    ? t('flow.history.assignment.person', 'Asignado a {{name}}.', { name })
    : t('flow.history.assignment.person-unknown', 'Asignado a una persona.');
  return [personLine, ruleLabel].filter(Boolean).join(' · ');
};

export const resolveHistoryEventType = (event = {}, t) => {
  if (isModificationEvent(event)) {
    return t('flow.history.type.edit', 'Edición');
  }
  if (event.ruleflow_id) {
    return t('flow.history.type.transition', 'Transición');
  }
  return t('flow.history.type.system', 'Sistema');
};

export const resolveHistoryActorLine = (event = {}, roles = [], t, usersById = {}) => {
  const roleLabel = resolveRoleLabel(event.catalogrole_id, roles, t);
  const userLabel = resolveUserLabel(event);
  const userId = event.user_id;

  if (userLabel && userLabel !== '—') {
    return `${roleLabel} · ${userLabel}${userId ? ` (#${userId})` : ''}`;
  }

  if (userId) {
    const fromMap = usersById[userId];
    if (fromMap) {
      return `${roleLabel} · ${resolveUserLabel({ user: fromMap })} (#${userId})`;
    }
    return `${roleLabel} · ${t('flow.history.user-id', 'Usuario')} #${userId}`;
  }

  return roleLabel;
};

export const buildHistoryEventDiffs = ({
  event,
  moduleProcess,
  flowEngine = {},
  roles = [],
  t,
  usersById = {},
  catalogLookups = {},
  hideReasonDetail = true
}) => {
  const diffs = formatDiffEntriesForDisplay({
    diffSnapshot: event?.diff_snapshot,
    fieldLabelResolver: (field) => buildModuleFieldLabel(moduleProcess, field, t),
    flowEngine,
    roles,
    t,
    recordStateCatalogId: event?.state_catalog_id,
    usersById,
    catalogLookups
  });

  if (!hideReasonDetail) {
    return diffs;
  }

  return diffs.filter((entry) => entry.field !== 'reason_detail');
};

export const buildTechnologyFieldLabel = (field, t) => {
  if (field === 'catalogproblem_list') {
    return t('technology.short.catalogproblem_list', 'Problemas socio climáticos');
  }
  return t(`technology.short.${field}`, field);
};
